import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Business.module.css";
import placeholderImg from "../../../assets/images/business-placeholder.jpg";
import businessImg from "../../../assets/images/business.png";
import api from "../../../api/axios";
import { toast } from "react-hot-toast";
import { 
  Store, Utensils, Heart, GraduationCap, Home, Settings, 
  Shirt, Car, Building, Laptop, Music, LayoutGrid, MoreHorizontal, Sparkles, Award, Star, Search
} from "lucide-react";

const categories = [
  { name: "All Businesses", icon: <LayoutGrid size={16} /> },
  { name: "Food & Catering", icon: <Utensils size={16} /> },
  { name: "Health & Wellness", icon: <Heart size={16} /> },
  { name: "Education & Coaching", icon: <GraduationCap size={16} /> },
  { name: "Fashion & Beauty", icon: <Shirt size={16} /> },
  { name: "Home & Decor", icon: <Home size={16} /> },
  { name: "Event & Wedding Services", icon: <Sparkles size={16} /> },
  { name: "Professional Services", icon: <Settings size={16} /> },
  { name: "Automotive", icon: <Car size={16} /> },
  { name: "Real Estate", icon: <Building size={16} /> },
  { name: "Technology & IT", icon: <Laptop size={16} /> },
  { name: "Retail & Shopping", icon: <Store size={16} /> },
  { name: "Travel & Tourism", icon: <LayoutGrid size={16} /> },
  { name: "Construction & Manufacturing", icon: <Building size={16} /> },
  { name: "Agriculture & Farming", icon: <LayoutGrid size={16} /> },
  { name: "Entertainment & Media", icon: <Music size={16} /> },
  { name: "More", icon: <MoreHorizontal size={16} /> },
];

const categoryColorMap = {
  Technology: "tech",
  "Health & Catering": "food",
  "Beauty & Salon": "wellness",
  "Home & Decor": "decor",
  "Health & Fitness": "wellness",
  "Home Services": "services",
  "Event Management": "entertainment",
  Education: "education",
};

const Business = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All Businesses");
  const [dynamicCategories, setDynamicCategories] = useState(categories);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCustomCategorySearch, setShowCustomCategorySearch] = useState(false);
  const [customCategoryQuery, setCustomCategoryQuery] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [reviewForm, setReviewForm] = useState({ reviewerName: '', rating: 5, comment: '' });
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);
  
  const navigate = useNavigate();

  const handleRegisterClick = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/resident/business');
    } else {
      navigate('/login');
    }
  };

  const getImageUrl = (path) => {
    if (!path) return placeholderImg;
    if (path.startsWith('http')) return path;
    const normalized = path.replace(/\\/g, '/');
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
  };

  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchBusinessesAndCategories = async () => {
      try {
        const { data } = await api.get('/public/businesses');
        setBusinesses(data);

        const { data: catData } = await api.get('/public/businesses/categories');
        const existingCatNames = categories.map(c => c.name);
        const newCats = catData.filter(c => !existingCatNames.includes(c) && c !== 'Other' && c !== 'All Businesses' && c !== 'More');
        
        if (newCats.length > 0) {
          const customCatObjects = newCats.map(c => ({ name: c, icon: <Store size={16} /> }));
          const updatedCategories = [...categories];
          updatedCategories.splice(updatedCategories.length - 1, 0, ...customCatObjects);
          setDynamicCategories(updatedCategories);
        }
      } catch (error) {
        console.error("Failed to fetch businesses or categories", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessesAndCategories();
  }, []);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.reviewerName.trim() || !reviewForm.rating) {
      toast.error("Name and rating are required");
      return;
    }
    try {
      setSubmittingReview(true);
      const res = await api.post(`/public/businesses/${selectedBusiness._id}/review`, reviewForm);
      toast.success("Review submitted successfully!");
      
      setSelectedBusiness(res.data.business);
      setBusinesses(prev => prev.map(b => b._id === res.data.business._id ? res.data.business : b));
      setReviewForm({ reviewerName: '', rating: 5, comment: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const filteredBusinesses = useMemo(() => {
    return businesses.filter(b => {
      let matchCategory = activeCategory === "All Businesses" || b.category === activeCategory;
      
      if (showCustomCategorySearch && customCategoryQuery.trim() !== "") {
         matchCategory = b.category.toLowerCase().includes(customCategoryQuery.toLowerCase());
      } else if (showCustomCategorySearch && customCategoryQuery.trim() === "") {
         matchCategory = true;
      }

      const matchSearch = b.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (b.description && b.description.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchCategory && matchSearch;
    });
  }, [businesses, activeCategory, searchQuery, showCustomCategorySearch, customCategoryQuery]);

  const featuredBusinesses = filteredBusinesses.filter((b) => b.featured);

  const renderStars = (rating) => {
    return (
      <span className={styles.starIcon}>
        <svg viewBox="0 0 24 24" fill="currentColor">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
      </span>
    );
  };

  return (
    <div className={styles.businessPage}>
      {/* 1. Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.pillBadge}><Sparkles size={14} style={{ display: 'inline', marginRight: '4px' }} /> CONNECT. COLLABORATE. GROW.</div>
          <h1 className={styles.heroTitle}>
            Discover Businesses Within Our Community
          </h1>
          <p className={styles.heroSubtitle}>
            Explore a diverse network of trusted businesses run by members of
            Sanskriti Shubh Aangan. Support local. Support our community.
          </p>
          <div className={styles.heroButtons}>
            <button className={styles.primaryBtn} onClick={handleRegisterClick}>
              <Award size={18} style={{ display: 'inline', marginRight: '6px' }} /> Register Your Business &rarr;
            </button>
          </div>
        </div>
        <div className={styles.heroImageContainer}>
          <img
            src={businessImg}
            alt="Community Business"
            className={styles.heroImage}
          />
        </div>
      </section>

      {/* Search Bar UI */}
      <section className={styles.categorySection}>
        <div className={styles.searchContainerWrapper}>
          <div className={styles.searchBar}>
            
            {/* Category Dropdown */}
            <div className={styles.categoryDropdownContainer} ref={dropdownRef}>
              <button 
                className={styles.dropdownToggle}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className={styles.activeCategoryDisplay}>
                  {dynamicCategories.find(c => c.name === activeCategory)?.icon || <LayoutGrid size={18} />}
                  <span>{activeCategory === "All Businesses" ? "All Categories" : activeCategory}</span>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              {isDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {dynamicCategories.filter(c => c.name !== "More").map((cat) => (
                    <div 
                      key={cat.name} 
                      className={`${styles.dropdownItem} ${activeCategory === cat.name ? styles.dropdownItemActive : ''}`}
                      onClick={() => {
                        setActiveCategory(cat.name);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className={styles.dropdownItemIcon}>{cat.icon || <Store size={16} />}</span>
                      <span>{cat.name === "All Businesses" ? "All Categories" : cat.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className={styles.searchDivider}></div>

            {/* Search Input */}
            <div className={styles.searchInputWrapper}>
              <Search size={20} className={styles.searchIcon} />
              <input 
                type="text" 
                placeholder="Search businesses, services or professionals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            {/* Search Button */}
            <button className={styles.searchActionBtn}>
              <Search size={18} /> Search
            </button>
          </div>
        </div>
      </section>

      {/* 4. Featured Businesses (Only show if there are featured businesses) */}
      {!loading && featuredBusinesses.length > 0 && (
        <section className={styles.featuredSection}>
          <h2 className={styles.sectionTitle}>Featured Community Businesses</h2>
          <p className={styles.sectionSubtitle}>
            Trusted businesses from our community
          </p>

          <div className={styles.featuredGrid}>
            {featuredBusinesses.map((business) => (
              <div key={business._id} className={styles.businessCard} onClick={() => setSelectedBusiness(business)} style={{ cursor: 'pointer' }}>
                <div className={styles.cardCover}>
                  <img src={getImageUrl(business.image)} alt="cover" loading="lazy" />
                  <span className={styles.categoryPill}>{business.category}</span>
                </div>
                <div className={styles.cardContent}>
                  <div className={styles.cardHeader}>
                    <div className={styles.businessLogo}>
                      {business.logo ? (
                        <img src={getImageUrl(business.logo)} alt="logo" loading="lazy" />
                      ) : (
                        business.name.charAt(0)
                      )}
                    </div>
                  </div>
                  <h3 className={styles.businessName}>{business.name}</h3>
                  <span className={styles.businessCategoryLabel}>
                    {business.category}
                  </span>
                  <p className={styles.businessLocationText}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    {business.location || "N/A"}
                  </p>
                  <p className={styles.businessDesc}>{business.description}</p>

                  <div className={styles.cardFooter}>
                    <span className={styles.ratingInfo}>
                      {renderStars(business.rating)}
                      {business.rating || 0}{" "}
                      <span className={styles.reviewCount}>
                        ({business.reviews || 0} Reviews)
                      </span>
                    </span>
                    <button
                      className={styles.viewBtn}
                      onClick={(e) => { e.stopPropagation(); setSelectedBusiness(business); }}
                    >
                      View Details <Sparkles size={14} style={{ display: 'inline', marginLeft: '4px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 5. All Businesses Section */}
      <section className={styles.allBusinessesSection}>
        <h2 className={styles.sectionTitle}>All Community Businesses</h2>

        <div className={styles.allBusinessesGrid}>
          {loading ? (
             <p style={{ gridColumn: "1 / -1", textAlign: "center" }}>Loading...</p>
          ) : filteredBusinesses.length > 0 ? (
            filteredBusinesses.map((business) => (
              <div key={business._id} className={styles.simpleBusinessCard} onClick={() => setSelectedBusiness(business)} style={{ cursor: 'pointer' }}>
                <div className={styles.simpleCardImage}>
                  <img src={getImageUrl(business.image)} alt={business.name} />
                </div>
                <div className={styles.simpleCardContent}>
                  <h3 className={styles.simpleBusinessName}>{business.name}</h3>
                  <span
                    className={`${styles.businessCategoryLabel} ${styles[categoryColorMap[business.category]] || ""}`}
                  >
                    {business.category}
                  </span>
                  <p className={styles.businessLocationText}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    {business.location || "N/A"}
                  </p>
                  <p className={styles.simpleBusinessDesc}>
                    {business.description}
                  </p>

                  <div className={styles.simpleCardFooter}>
                    <span className={styles.ratingInfo}>
                      {renderStars(business.rating)}
                      {business.rating || 0}
                    </span>
                    <button
                      className={styles.outlineBtn}
                      onClick={(e) => { e.stopPropagation(); setSelectedBusiness(business); }}
                    >
                      View Details <Sparkles size={14} style={{ display: 'inline', marginLeft: '4px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '16px', gridColumn: '1 / -1', minHeight: '200px' }}>
              <Store size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', color: '#475569', marginBottom: '8px' }}>No Businesses Found</h3>
              <p style={{ color: '#64748b', textAlign: 'center', margin: 0, fontSize: '14px' }}>No businesses match your current search/filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* 6. CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2><Sparkles size={24} style={{ display: 'inline', marginRight: '8px' }} /> Are You a Business Owner? <Sparkles size={24} style={{ display: 'inline', marginLeft: '8px' }} /></h2>
          <p>Showcase your business and connect with your community.</p>
          <button
            className={styles.ctaBtn}
            onClick={handleRegisterClick}
          >
            Register Your Business →
          </button>
        </div>
      </section>

      {/* Business Details Modal */}
      {selectedBusiness && (
        <div
          className={styles.modalOverlay}
          onClick={() => setSelectedBusiness(null)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeBtn}
              onClick={() => setSelectedBusiness(null)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <div className={styles.modalHeader}>
              <div className={styles.modalCover}>
                <div 
                  className={styles.modalCoverBlur} 
                  style={{ backgroundImage: `url(${getImageUrl(selectedBusiness.image)})` }} 
                />
                <img src={getImageUrl(selectedBusiness.image)} alt="cover" className={styles.modalCoverImg} />
              </div>
              <div className={styles.modalHeaderInfo}>
                <div className={styles.modalLogo}>
                  {selectedBusiness.logo ? (
                    <img src={getImageUrl(selectedBusiness.logo)} alt="logo" />
                  ) : (
                    selectedBusiness.name.charAt(0)
                  )}
                </div>
                <div className={styles.modalTitleArea}>
                  <h2>{selectedBusiness.name}</h2>
                  <div className={styles.modalTags}>
                    <span className={styles.categoryTag}>
                      {selectedBusiness.category}
                    </span>
                    <span className={styles.ratingTag}>
                      {renderStars(selectedBusiness.rating)}
                      {selectedBusiness.rating || 0} ({selectedBusiness.reviews || 0}{" "}
                      Reviews)
                    </span>
                  </div>
                  <p className={styles.modalOwner}>
                    Owner: <strong>{selectedBusiness.ownerName || selectedBusiness.owner}</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalSection}>
                <h3>About</h3>
                <p>{selectedBusiness.description}</p>
              </div>

              {selectedBusiness.services && selectedBusiness.services.length > 0 && (
                <div className={styles.modalSection}>
                  <h3>Services</h3>
                  <div className={styles.servicesGrid}>
                    {selectedBusiness.services.map((srv, idx) => (
                      <div key={idx} className={styles.serviceItem}>
                        {srv}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.modalSection}>
                <h3>Business Information</h3>
                <ul className={styles.infoList}>
                  <li>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    {selectedBusiness.phone}
                  </li>
                  <li>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    {selectedBusiness.email}
                  </li>
                  <li>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    {selectedBusiness.workingHours}
                  </li>
                  <li>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    {selectedBusiness.location}
                  </li>
                  <li>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="2" y1="12" x2="22" y2="12"></line>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                    </svg>
                    {selectedBusiness.website}
                  </li>
                </ul>
              </div>

              <div className={styles.modalSection}>
                <h3>Reviews & Ratings</h3>
                
                {selectedBusiness.reviewList && selectedBusiness.reviewList.length > 0 ? (
                  <div className={styles.reviewsList} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {selectedBusiness.reviewList.map((review, idx) => (
                      <div key={idx} className={styles.reviewItem} style={{ paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <strong style={{ color: '#1e293b' }}>{review.reviewerName}</strong>
                          <span style={{ color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                            <Star size={14} fill="#fbbf24" stroke="none" /> {review.rating}
                          </span>
                        </div>
                        {review.comment && <p style={{ margin: '0 0 6px 0', fontSize: '0.95rem', color: '#475569', lineHeight: '1.4' }}>{review.comment}</p>}
                        <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                          {`${new Date(review.date).getDate()}/${new Date(review.date).getMonth() + 1}/${new Date(review.date).getFullYear()}`}
                        </small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic' }}>No reviews yet. Be the first to review!</p>
                )}

                <div className={styles.writeReviewForm} style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '1.05rem', color: '#0f172a' }}>Write a Review</h4>
                  <form onSubmit={handleReviewSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <input 
                        type="text" 
                        placeholder="Your Name *" 
                        value={reviewForm.reviewerName}
                        onChange={(e) => setReviewForm({...reviewForm, reviewerName: e.target.value})}
                        required
                        style={{ flex: 1, minWidth: '150px', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem' }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '42px' }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={28}
                            style={{ 
                              cursor: 'pointer', 
                              transition: 'all 0.2s', 
                              fill: (hoverRating || reviewForm.rating) >= star ? '#eab308' : 'transparent', 
                              color: (hoverRating || reviewForm.rating) >= star ? '#eab308' : '#cbd5e1',
                              transform: hoverRating === star ? 'scale(1.1)' : 'scale(1)'
                            }}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                          />
                        ))}
                      </div>
                    </div>
                    <textarea 
                      placeholder="Write your experience... (optional)"
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                      rows="3"
                      style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '6px', resize: 'vertical', fontSize: '0.95rem', fontFamily: 'inherit' }}
                    />
                    <button 
                      type="submit" 
                      disabled={submittingReview}
                      style={{ padding: '12px', background: '#014d33', color: 'white', border: 'none', borderRadius: '6px', cursor: submittingReview ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '1rem', transition: 'background 0.2s', opacity: submittingReview ? 0.7 : 1 }}
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </button>
                  </form>
                </div>
              </div>

              <div className={styles.actionButtons}>
                <button 
                  className={styles.callBtn}
                  onClick={() => selectedBusiness.phone && (window.location.href = `tel:${selectedBusiness.phone}`)}
                >
                  Call
                </button>
                <button 
                  className={styles.whatsappBtn}
                  onClick={() => {
                    if (selectedBusiness.phone) {
                      const phone = selectedBusiness.phone.replace(/\D/g, '');
                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent('Hi, I found your business on Sanskriti Shubh Aangan community. I would like to know more.')}`, '_blank');
                    }
                  }}
                >
                  WhatsApp
                </button>
                <button 
                  className={styles.emailBtn}
                  onClick={() => selectedBusiness.email && (window.location.href = `mailto:${selectedBusiness.email}?subject=${encodeURIComponent('Inquiry from Sanskriti Shubh Aangan Community')}`)}
                >
                  Email
                </button>
                <button 
                  className={styles.shareBtn}
                  onClick={async () => {
                    const shareData = {
                      title: selectedBusiness.name,
                      text: `Check out ${selectedBusiness.name} on Sanskriti Shubh Aangan Community!`,
                      url: window.location.href,
                    };
                    try {
                      if (navigator.share) {
                        await navigator.share(shareData);
                      } else {
                        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
                        import('react-hot-toast').then(module => module.toast.success("Business details copied to clipboard!"));
                      }
                    } catch (err) {
                      console.error("Error sharing:", err);
                    }
                  }}
                >
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Business;
